// CZ: fleet vehicle default settings with options the client picks from in the
// coverage request (MTPL insurance limits, hull deductible options). Such a
// setting applies to all vehicles of its insurer (settingCriteria = {}); the
// applicable one for a requested line is the insurer's setting with the latest
// valid from date that is not after the line's coverage start.

import { FleetDefaultSetting } from "types/types";

// "YYYY-MM-DD" of a setting's validFromDate (an ISO string from the backend,
// or a Date), without timezone shifts.
const toIsoDay = (value: Date | string | null | undefined): string | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const match = /^\d{4}-\d{2}-\d{2}/.exec(value);
    if (match) return match[0];
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * The insurer's `attributeName` setting applicable on `coverageFrom`
 * ("YYYY-MM-DD"): the latest valid from date not after it (on the same day the
 * newer setting). null when there is no insurer, no coverage start or no such
 * setting.
 */
export const findApplicableClientOptionSetting = (
  fleetDefaultSettings: FleetDefaultSetting[] | null | undefined,
  attributeName: FleetDefaultSetting["attributeName"],
  insurerPartnerNumber: number | string | null | undefined,
  coverageFrom: string | null | undefined,
): FleetDefaultSetting | null => {
  const insurer = Number(insurerPartnerNumber);
  if (!fleetDefaultSettings || !insurer || !coverageFrom) return null;

  let applicable: FleetDefaultSetting | null = null;
  let applicableFrom: string | null = null;
  fleetDefaultSettings.forEach((s) => {
    if (
      s.attributeName !== attributeName ||
      Number(s.insurerPartnerNumber) !== insurer
    ) {
      return;
    }
    const from = toIsoDay(s.validFromDate);
    // "YYYY-MM-DD" strings compare chronologically
    if (!from || from > coverageFrom) return;
    if (
      !applicable ||
      from > applicableFrom! ||
      (from === applicableFrom &&
        s.fleetVehicleDefaultSettingId >
          applicable.fleetVehicleDefaultSettingId)
    ) {
      applicable = s;
      applicableFrom = from;
    }
  });
  return applicable;
};

/** The parsed JSON list of a setting's attributeValue; [] if it is not a list. */
export const parseClientOptionList = (
  attributeValue: string | number | null | undefined,
): any[] => {
  if (typeof attributeValue !== "string" || !attributeValue) return [];
  try {
    const parsed = JSON.parse(attributeValue);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
